"use client";

import { cn } from "@newxsystems/shared";
import { useId, useRef, useState } from "react";
import {
  Button,
  Checkbox,
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
  Input,
  Textarea,
} from "@templates/design-system";
import type { FormField } from "@newxsystems/types";
import { getFormSubmitter } from "../../../../client";
import { BlockActions } from "../../../shared/components/BlockActions/BlockActions";
import { BlockCommonContent } from "../../../shared/components/BlockCommonContent/BlockCommonContent";
import { BlockContainer } from "../../../shared/components/BlockContainer/BlockContainer";
import { BlockRichText } from "../../../shared/components/BlockRichText/BlockRichText";
import type { IFormStandardProps } from "./FormStandard.props";

type FieldValue = string | boolean;
type FieldErrors = Partial<Record<number, string>>;
type SubmissionState = "ready" | "submitting" | "success" | "error";

const initialValueFor = (field: FormField): FieldValue =>
  field.fieldType === "checkbox" ? false : "";

const validateField = (field: FormField, value: FieldValue): string | undefined => {
  if (field.required) {
    const isEmpty = typeof value === "boolean" ? !value : !value.trim();

    if (isEmpty) {
      if (field.fieldType === "checkbox") {
        return `${field.label} must be checked.`;
      }

      if (field.fieldType === "select") {
        return `Select an option for ${field.label}.`;
      }

      return `${field.label} is required.`;
    }
  }

  if (field.fieldType === "email" && typeof value === "string" && value) {
    const emailControl = document.createElement("input");
    emailControl.type = "email";
    emailControl.value = value;

    if (!emailControl.validity.valid) {
      return `Enter a valid email address for ${field.label}.`;
    }
  }

  return undefined;
};

export const FormStandard = ({
  alignment,
  anchorId,
  buttons,
  description,
  eyebrow,
  fields,
  heading,
  spacing,
  submitLabel,
  successMessage,
  errorMessage,
}: IFormStandardProps) => {
  const identity = useId();
  const controls = useRef<Array<HTMLElement | null>>([]);
  const submissionLock = useRef(false);
  const [values, setValues] = useState<FieldValue[]>(() => fields.map(initialValueFor));
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submissionState, setSubmissionState] = useState<SubmissionState>("ready");
  const hasCommonContent = Boolean(eyebrow || heading || description || buttons?.length);

  const updateValue = (index: number, value: FieldValue) => {
    setValues((currentValues) =>
      currentValues.map((currentValue, valueIndex) =>
        valueIndex === index ? value : currentValue,
      ),
    );
    setErrors((currentErrors) => {
      if (!currentErrors[index]) {
        return currentErrors;
      }

      const nextErrors = { ...currentErrors };
      delete nextErrors[index];
      return nextErrors;
    });
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (submissionState === "submitting" || submissionLock.current) {
      return;
    }

    setSubmissionState("ready");

    const nextErrors: FieldErrors = {};
    fields.forEach((field, index) => {
      const message = validateField(field, values[index] ?? initialValueFor(field));
      if (message) {
        nextErrors[index] = message;
      }
    });

    setErrors(nextErrors);

    const firstInvalidIndex = fields.findIndex((_, index) => Boolean(nextErrors[index]));
    if (firstInvalidIndex >= 0) {
      controls.current[firstInvalidIndex]?.focus();
      return;
    }

    submissionLock.current = true;
    setSubmissionState("submitting");
    const submitter = getFormSubmitter();
    if (!submitter) {
      submissionLock.current = false;
      setSubmissionState("error");
      return;
    }

    try {
      await submitter({
        fields: fields.map((field, index) => ({
          label: field.label,
          fieldType: field.fieldType,
          value: values[index] ?? initialValueFor(field),
        })),
        anchorId: anchorId ?? undefined,
      });
      submissionLock.current = false;
      setSubmissionState("success");
    } catch {
      submissionLock.current = false;
      setSubmissionState("error");
    }
  };

  const isSubmitting = submissionState === "submitting";
  const formClassName = cn(
    hasCommonContent ? "mt-12 max-w-2xl md:mt-16" : "max-w-2xl",
    alignment === "center" && "mx-auto",
  );

  return (
    <BlockContainer anchorId={anchorId} spacing={spacing}>
      <BlockCommonContent
        alignment={alignment}
        description={description}
        eyebrow={eyebrow}
        heading={heading}
        headingLevel="h2"
      />
      {buttons?.length ? (
        <BlockActions actions={buttons} alignment={alignment} className="mt-8" />
      ) : null}
      {submissionState === "success" ? (
        <div className={formClassName} role="status">
          <BlockRichText
            className="max-w-[62ch] text-base/[1.55] [&_a]:underline [&_ol]:ml-5 [&_ol]:list-decimal [&_p+p]:mt-4 [&_ul]:ml-5 [&_ul]:list-disc"
            content={successMessage}
          />
        </div>
      ) : (
        <form aria-busy={isSubmitting} className={formClassName} noValidate onSubmit={handleSubmit}>
          {submissionState === "error" ? (
            <div role="alert">
              <BlockRichText
                className="max-w-[62ch] text-base/[1.55] [&_a]:underline [&_ol]:ml-5 [&_ol]:list-decimal [&_p+p]:mt-4 [&_ul]:ml-5 [&_ul]:list-disc"
                content={errorMessage}
              />
            </div>
          ) : null}
          <div aria-atomic="true" aria-live="polite" className="sr-only" role="status">
            {isSubmitting ? "Submitting…" : null}
          </div>
          <FieldGroup>
            {fields.map((field, index) => {
              const controlId = `${identity}-field-${index}`;
              const errorId = `${controlId}-error`;
              const error = errors[index];
              const describedBy = error ? errorId : undefined;
              const accessibleLabel = `${field.label}${field.required ? "(required)" : ""}`;
              const setControl = (node: HTMLElement | null) => {
                controls.current[index] = node;
              };

              if (field.fieldType === "checkbox") {
                return (
                  <Field data-invalid={Boolean(error)} key={`${identity}-${index}`}>
                    <div className="flex items-start gap-3">
                      <Checkbox
                        aria-describedby={describedBy}
                        aria-invalid={Boolean(error)}
                        aria-label={accessibleLabel}
                        checked={values[index] === true}
                        id={controlId}
                        onCheckedChange={(checked) => updateValue(index, checked === true)}
                        ref={setControl}
                        required={field.required === true}
                      />
                      <FieldLabel className="pt-0.5 leading-snug" htmlFor={controlId}>
                        {field.label}
                        {field.required ? <span>(required)</span> : null}
                      </FieldLabel>
                    </div>
                    <FieldError id={errorId}>{error}</FieldError>
                  </Field>
                );
              }

              const commonControlProps = {
                "aria-describedby": describedBy,
                "aria-invalid": Boolean(error),
                "aria-label": accessibleLabel,
                id: controlId,
                name: controlId,
                onChange: (
                  event: React.ChangeEvent<
                    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
                  >,
                ) => updateValue(index, event.target.value),
                placeholder: field.placeholder ?? undefined,
                ref: setControl,
                required: field.required === true,
                value: typeof values[index] === "string" ? values[index] : "",
              };

              return (
                <Field data-invalid={Boolean(error)} key={`${identity}-${index}`}>
                  <FieldLabel htmlFor={controlId}>
                    {field.label}
                    {field.required ? <span>(required)</span> : null}
                  </FieldLabel>
                  {field.fieldType === "long-text" ? (
                    <Textarea {...commonControlProps} rows={5} />
                  ) : field.fieldType === "select" ? (
                    <select
                      {...commonControlProps}
                      className="border-input focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 flex h-10 w-full rounded-md border bg-background px-3 py-2 text-base shadow-xs outline-none focus-visible:ring-[3px] md:text-sm"
                    >
                      <option disabled hidden value="">
                        {field.placeholder ?? ""}
                      </option>
                      {field.selectOptions?.map((option, optionIndex) => (
                        <option key={`${option.label}-${optionIndex}`} value={option.label}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <Input
                      {...commonControlProps}
                      type={
                        field.fieldType === "email"
                          ? "email"
                          : field.fieldType === "phone"
                            ? "tel"
                            : "text"
                      }
                    />
                  )}
                  <FieldError id={errorId}>{error}</FieldError>
                </Field>
              );
            })}
          </FieldGroup>
          <Button className="mt-8" disabled={isSubmitting} type="submit">
            {isSubmitting ? "Submitting…" : submitLabel}
          </Button>
        </form>
      )}
    </BlockContainer>
  );
};

FormStandard.displayName = "Form::FormStandard";
